/**
 * Canal WS de notificaciones in-app en vivo (S46 · Wave C, O7).
 *
 * Contrato `GET /ws/notifications`:
 * - Auth con `?token=<JWT access>` (BREAKING S46: `?userId=` ya no se acepta).
 * - Al conectar el server emite `{"type":"notification.ws.ready"}`.
 * - Push: `{"type":"notification.created", id, category, title, body,
 *   priority, status, resourceType, resourceId, createdAt, subject}`.
 * - Close 4401 = token inválido → refrescar token y reconectar.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ENV } from "@/config/environment";
import { useAuth } from "@/context/AuthContext";
import { getCurrentAccessToken } from "@/services/auth";

const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;

export const NOTIFICATION_CREATED_EVENT = "aequia:notification.created";

export type RealtimeResourceType = "DOCUMENT" | "MATTER" | "CLIENT" | null;

export interface RealtimeNotification {
  type: "notification.created";
  id: string;
  category?: string | null;
  title?: string | null;
  body?: string | null;
  priority?: string | null;
  status?: string | null;
  resourceType?: RealtimeResourceType;
  resourceId?: string | null;
  createdAt?: string | null;
  subject?: string | null;
}

interface RealtimeReadyMessage {
  type: "notification.ws.ready";
}

type RealtimeMessage = RealtimeNotification | RealtimeReadyMessage;

function buildWsUrl(token: string): string {
  const base = ENV.backendUrl.startsWith("http")
    ? ENV.backendUrl
    : `${window.location.origin}${ENV.backendUrl}`;
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname.replace(/\/$/, "")}/ws/notifications`;
  url.searchParams.set("token", token);
  return url.toString();
}

export function useRealtimeNotifications() {
  const { user, currentTenantId } = useAuth();
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const userId = user?.id ?? null;
  const tenantKey = currentTenantId ?? "*";

  useEffect(() => {
    if (!userId) return undefined;
    let closed = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    let retryTimer: number | null = null;

    const scheduleReconnect = () => {
      if (closed) return;
      retryTimer = window.setTimeout(() => {
        void connect();
      }, retry);
      retry = Math.min(8000, retry * 1.8);
    };

    const connect = async () => {
      const token = await getCurrentAccessToken().catch(() => null);
      if (!token || closed) {
        setConnected(false);
        if (!closed) scheduleReconnect();
        return;
      }
      socket = new WebSocket(buildWsUrl(token));
      socket.onopen = () => {
        retry = 800;
        setConnected(true);
      };
      socket.onclose = (event: CloseEvent) => {
        setConnected(false);
        if (closed) return;
        if (event.code === WS_CLOSE_FORBIDDEN) return;
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          // El próximo connect pasa por getCurrentAccessToken → refresh.
          retry = Math.max(retry, 1200);
        }
        scheduleReconnect();
      };
      socket.onmessage = (event: MessageEvent<string>) => {
        let message: RealtimeMessage;
        try {
          message = JSON.parse(event.data) as RealtimeMessage;
        } catch {
          return;
        }
        if (message.type === "notification.ws.ready") {
          setConnected(true);
          return;
        }
        if (message.type === "notification.created") {
          // Badge en vivo: incrementa el unread-count cacheado y refresca
          // inbox; las pantallas interesadas escuchan el CustomEvent.
          qc.setQueryData<{ count: number } | undefined>(
            ["notifications", "unread-count", tenantKey],
            (current) =>
              current ? { count: current.count + 1 } : { count: 1 },
          );
          void qc.invalidateQueries({ queryKey: ["notifications"] });
          window.dispatchEvent(
            new CustomEvent<RealtimeNotification>(NOTIFICATION_CREATED_EVENT, {
              detail: message,
            }),
          );
        }
      };
    };

    void connect();
    return () => {
      closed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [qc, tenantKey, userId]);

  return { connected };
}
